/**
 * ChipMultiSelect — popover-based multi-select chip input.
 *
 * Trigger: the parent renders a tiny compact view (chips truncated). When the
 * trigger is clicked, this component opens a popover anchored next to it with:
 *   • full chips list (with × to remove)
 *   • search input
 *   • filtered options below
 *   • "+ Crear «xxx»" option if the search query has no exact match
 *
 * Click outside or ESC → closes. Changes propagate live via onChange.
 *
 * Props:
 *   value:          string[]               — currently selected items
 *   onChange:       (newValues: string[]) — called on every change
 *   catalog:        string[]               — available options to choose from
 *   onCatalogAdd:   (newValue: string) → Promise<string[]>  — server-add, returns new catalog
 *   placeholder?:   string                 — text shown when empty
 *   testid?:        string                 — root data-testid
 *   compactLimit?:  number                 — chips shown in compact view before "+N" (default 2)
 *   accent?:        'cmic' | 'amber'       — color theme (default cmic)
 *   readOnly?:      boolean
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus, X, Search } from 'lucide-react';

const accents = {
  cmic:  { chip: 'bg-cmic-100 text-cmic-800',  chipHover: 'hover:bg-cmic-200',  ring: 'focus:ring-cmic-300' },
  amber: { chip: 'bg-amber-100 text-amber-800', chipHover: 'hover:bg-amber-200', ring: 'focus:ring-amber-300' },
};

export default function ChipMultiSelect({
  value = [],
  onChange,
  catalog = [],
  onCatalogAdd,
  placeholder = 'Sin valores',
  testid,
  compactLimit = 2,
  accent = 'cmic',
  readOnly = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pos, setPos] = useState(null); // {top, left, width} viewport coords
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const a = accents[accent] || accents.cmic;

  // Position the popover next to the trigger using viewport coordinates so it
  // escapes any overflow:hidden parents (table cells, scroll containers, …).
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    function place() {
      const rect = triggerRef.current.getBoundingClientRect();
      const popWidth = Math.max(rect.width, 280);
      const viewportW = window.innerWidth;
      let left = rect.left;
      // Flip to the right edge if the popover would overflow the viewport
      if (left + popWidth > viewportW - 8) {
        left = Math.max(8, viewportW - popWidth - 8);
      }
      // If there isn't enough room below, anchor above the trigger
      const viewportH = window.innerHeight;
      const popMaxHeight = 320;
      let top = rect.bottom + 4;
      if (top + popMaxHeight > viewportH - 8 && rect.top > popMaxHeight + 8) {
        top = rect.top - popMaxHeight - 4;
      }
      setPos({ top, left, width: popWidth });
    }
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true); // capture phase: catch table-internal scroll too
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  // Close on click outside / ESC
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (popoverRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selectedSet = useMemo(() => new Set((value || []).map((v) => String(v).toLowerCase())), [value]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return (catalog || []).filter((c) => !selectedSet.has(c.toLowerCase()));
    return (catalog || []).filter((c) => c.toLowerCase().includes(q) && !selectedSet.has(c.toLowerCase()));
  }, [catalog, query, selectedSet]);

  const queryHasExactMatch = query.trim() && (catalog || []).some((c) => c.toLowerCase() === query.trim().toLowerCase());
  const canCreate = !!onCatalogAdd && query.trim() && !queryHasExactMatch && !selectedSet.has(query.trim().toLowerCase());

  function addValue(v) {
    const next = [...(value || []), v];
    onChange?.(next);
    setQuery('');
  }
  function removeValue(v) {
    const next = (value || []).filter((x) => String(x).toLowerCase() !== String(v).toLowerCase());
    onChange?.(next);
  }
  async function createAndAdd() {
    const v = query.trim();
    if (!v || !onCatalogAdd) return;
    try { await onCatalogAdd(v); } catch (e) { /* keep going, still add locally */ }
    addValue(v);
  }

  return (
    <div className="relative" data-testid={testid}>
      {/* Trigger / compact view */}
      <button
        type="button"
        ref={triggerRef}
        onClick={() => !readOnly && setOpen((v) => !v)}
        data-testid={testid ? `${testid}-trigger` : undefined}
        disabled={readOnly}
        className={`w-full min-h-[28px] flex items-center flex-wrap gap-1 text-xs border border-transparent rounded px-1.5 py-1 outline-none focus:border-cmic-300 ${open ? 'border-cmic-300 bg-white' : 'hover:border-gray-200'} ${a.ring}`}
      >
        {(value || []).slice(0, compactLimit).map((v, i) => (
          <span key={`${v}-${i}`} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${a.chip}`}>{v}</span>
        ))}
        {(value || []).length > compactLimit && (
          <span className="text-[10px] font-semibold text-charcoal-500 bg-gray-100 px-1.5 py-0.5 rounded">+{value.length - compactLimit}</span>
        )}
        {(!value || value.length === 0) && <span className="text-[10px] text-charcoal-400 italic">{placeholder}</span>}
        <Plus size={11} className="text-charcoal-400 ml-auto" />
      </button>

      {/* Popover (portal-rendered, fixed-position so it escapes overflow:hidden parents) */}
      {open && pos && createPortal(
        <div
          ref={popoverRef}
          data-testid={testid ? `${testid}-popover` : undefined}
          style={{ position: 'fixed', top: pos.top, left: pos.left, minWidth: pos.width }}
          className="z-[100] max-w-[420px] bg-white border border-gray-200 rounded-xl shadow-xl p-3"
        >
          {/* Selected chips */}
          <div className="flex flex-wrap gap-1.5 mb-3 min-h-[28px]">
            {(value || []).length === 0 && (
              <span className="text-[11px] text-charcoal-400 italic self-center">Aún no seleccionas nada.</span>
            )}
            {(value || []).map((v, i) => (
              <span
                key={`${v}-${i}`}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${a.chip}`}
                data-testid={testid ? `${testid}-chip-${i}` : undefined}
              >
                {v}
                <button
                  type="button"
                  onClick={() => removeValue(v)}
                  className={`-mr-0.5 rounded p-0.5 ${a.chipHover}`}
                  data-testid={testid ? `${testid}-chip-remove-${i}` : undefined}
                  aria-label={`Quitar ${v}`}
                >
                  <X size={10} />
                </button>
              </span>
            ))}
          </div>

          {/* Search input */}
          <div className="relative mb-2">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-charcoal-400" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && canCreate) { e.preventDefault(); createAndAdd(); } }}
              placeholder="Buscar o crear nuevo…"
              data-testid={testid ? `${testid}-search` : undefined}
              className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-cmic-300"
            />
          </div>

          {/* Filtered options */}
          <div className="max-h-48 overflow-y-auto -mx-1">
            {filtered.length === 0 && !canCreate && (
              <div className="text-[11px] text-charcoal-400 italic px-2 py-2 text-center">Sin coincidencias.</div>
            )}
            {filtered.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => addValue(opt)}
                data-testid={testid ? `${testid}-option-${opt.replace(/\W+/g, '-').toLowerCase()}` : undefined}
                className="w-full text-left px-2 py-1.5 text-xs hover:bg-cmic-50 rounded text-charcoal-700"
              >
                {opt}
              </button>
            ))}
            {canCreate && (
              <button
                type="button"
                onClick={createAndAdd}
                data-testid={testid ? `${testid}-create` : undefined}
                className="w-full text-left px-2 py-1.5 text-xs rounded text-cmic-700 hover:bg-cmic-50 font-medium border-t border-gray-100 mt-1 pt-2 flex items-center gap-1.5"
              >
                <Plus size={11} /> Crear «{query.trim()}»
              </button>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
